import { useCallback, useMemo } from 'react';

import 'dayjs/locale/de';
import 'dayjs/locale/en';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

import { localeDateFormats, localeDateWithTimeFormats, SupportedLanguages } from '../i18n/constants';
import { getUserLanguage } from '../i18n/helpers';
import { toBoolean } from '../pages/Landing/utils';
import { useStores } from '../stores/useStores';
import { localeAmount } from '../utils/helpers';
import { devError } from '../utils/log';

export const useLocale = () => {
  const { userStore } = useStores();
  const { profile: userProfile } = userStore;

  const isGDPR = useMemo(() => {
    return toBoolean(userProfile?.territory?.country?.cookieConsentEnabled);
  }, [userProfile]);

  const locale = useMemo(() => {
    return getUserLanguage(userProfile?.language);
  }, [userProfile]);

  const localeDateFormat = useMemo(() => {
    return locale in localeDateFormats ? localeDateFormats[locale] : localeDateFormats[SupportedLanguages.EN];
  }, [locale]);

  const localeDateWithTimeFormat = useMemo(() => {
    return locale in localeDateWithTimeFormats
      ? localeDateWithTimeFormats[locale]
      : localeDateWithTimeFormats[SupportedLanguages.EN];
  }, [locale]);

  const localeCurrencySymbol = useCallback(() => {
    return userProfile?.currency?.symbol;
  }, [userProfile]);

  const localeCurrency = useCallback(
    (amount: number) => {
      const { currency, territory } = userProfile || {};
      try {
        if (currency?.code && territory?.country.code) {
          return localeAmount({
            amount,
            currency: currency.code,
            countryCode: territory.country.code,
          });
        } else {
          return amount.toLocaleString();
        }
      } catch (error) {
        devError('Error in localeCurrency', error);
        return amount.toLocaleString();
      }
    },
    [userProfile],
  );

  const formatDate = useCallback(
    (date: Date | string, options?: { withTime?: boolean }) => {
      return dayjs(date).format(options?.withTime ? localeDateWithTimeFormat : localeDateFormat);
    },
    [localeDateFormat, localeDateWithTimeFormat],
  );

  const localeDate = useCallback(
    (date: Date | string) => {
      return dayjs(date).locale(locale.toLowerCase());
    },
    [locale],
  );

  return {
    localeCurrency,
    formatDate,
    localeDate,
    localeDateFormat,
    localeDateWithTimeFormat,
    isGDPR,
    localeCurrencySymbol,
    locale,
  };
};
